/**
 * Grid — the invisible structure every template snaps to.
 *
 * A layout looks professional mostly because edges line up. Rather than trust
 * that, templates ask the grid for x positions and column spans, so alignment
 * is arithmetic rather than intention.
 */

import { inch } from './tokens';

/**
 * Build a 12-column grid for a canvas.
 *
 * @param canvas  { width, height }
 * @param opts.margin   side margin in px (default 0.75in — generous, which is
 *                      itself part of reading as premium)
 * @param opts.columns  column count
 * @param opts.gutter   space between columns
 */
export const grid = (canvas, { margin = inch(0.75), columns = 12, gutter = inch(0.16) } = {}) => {
  const contentWidth = canvas.width - margin * 2;
  const colWidth = (contentWidth - gutter * (columns - 1)) / columns;

  return {
    canvas,
    margin,
    columns,
    gutter,
    contentWidth,
    colWidth,

    /** Left edge of the content area. */
    left: margin,
    /** Right edge of the content area. */
    right: canvas.width - margin,
    /** Horizontal centre of the canvas. */
    centerX: canvas.width / 2,
    /** Bottom edge of the content area. */
    bottom: canvas.height - margin,

    /** x of column `n` (0-indexed). */
    x: (n) => Math.round(margin + n * (colWidth + gutter)),

    /** Width spanning `n` columns including the gutters between them. */
    span: (n) => Math.round(n * colWidth + (n - 1) * gutter),

    /** A fraction of canvas height — for horizontal band boundaries. */
    hy: (fraction) => Math.round(canvas.height * fraction),

    /** A fraction of canvas width — for full-bleed splits. */
    wx: (fraction) => Math.round(canvas.width * fraction),
  };
};
